import { NumberInput, Select, SimpleGrid, Textarea } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import HrTermVariantCheckboxes from 'components/forms/hr/HrTermVariantCheckboxes';
import { DateInput } from 'components/inputs/DateInput';
import { HrRelatedTermFormValues } from 'components/modals/hr-term-state/HrTermStateModal';

interface HrPersonTermFormProps {
  form: UseFormReturnType<HrRelatedTermFormValues>;
  termTypes: any[];
  isTablet: boolean;
  onCancel: () => void;
  onSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
}

/** Dedicated form for adding one HR term from a person's route. */
export default function HrPersonTermForm({ form, termTypes, isTablet, onCancel, onSubmit }: HrPersonTermFormProps) {
  return (
    <FormWrapper
      primaryButtonText="Vytvoriť"
      primaryIcon={<IconPlus stroke={1.5} />}
      secondaryButtonText="Zrušiť"
      onSecondaryClick={onCancel}
      skipSecondaryButtonConfirm={!form.isDirty()}
      onSubmit={onSubmit}
      loading={false}
    >
      <FormInputGroup groupTitle="Termín">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <Select
            size="lg"
            label="Typ termínu"
            withAsterisk
            data={termTypes.map((type) => ({
              value: String(type.termTypeId),
              label: type.name ?? `Typ ${type.termTypeId}`,
            }))}
            {...form.getInputProps('termTypeId')}
            onChange={(value) => {
              form.setFieldValue('termTypeId', value ?? '');
              const selected = termTypes.find((type) => String(type.termTypeId) === value);
              const certificateType = selected?.kind === 'technician-certificate';
              form.setFieldValue(
                'recurrenceQuantity',
                selected?.defaultRecurrence?.quantity ?? (certificateType ? 1 : 0)
              );
              form.setFieldValue(
                'recurrenceUnit',
                selected?.defaultRecurrence?.unit ?? (certificateType ? 'years' : 'none')
              );
              form.setFieldValue('checkedVariantIds', []);
            }}
          />
          <DateInput size="lg" label="Najbližší termín" withAsterisk {...form.getInputProps('nextDueDate')} />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <NumberInput
            size="lg"
            label="Opakovanie"
            min={1}
            disabled={form.values.recurrenceUnit === 'none'}
            {...form.getInputProps('recurrenceQuantity')}
          />
          <Select
            size="lg"
            label="Jednotka opakovania"
            data={[
              ...(termTypes.find((type) => String(type.termTypeId) === form.values.termTypeId)?.kind ===
              'technician-certificate'
                ? []
                : [{ value: 'none', label: 'Bez opakovania' }]),
              { value: 'days', label: 'Dni' },
              { value: 'weeks', label: 'Týždne' },
              { value: 'months', label: 'Mesiace' },
              { value: 'years', label: 'Roky' },
            ]}
            {...form.getInputProps('recurrenceUnit')}
            onChange={(value) => {
              const recurrenceUnit = value ?? 'none';
              form.setFieldValue('recurrenceUnit', recurrenceUnit);
              if (recurrenceUnit === 'none') {
                form.setFieldValue('recurrenceQuantity', 0);
              } else if (form.values.recurrenceQuantity < 1) {
                form.setFieldValue('recurrenceQuantity', 1);
              }
            }}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <Textarea size="lg" label="Poznámka" {...form.getInputProps('note')} />
        </SimpleGrid>
        <HrTermVariantCheckboxes
          variants={termTypes.find((type) => String(type.termTypeId) === form.values.termTypeId)?.variants ?? []}
          checkedVariantIds={form.values.checkedVariantIds}
          onChange={(variantIds) => form.setFieldValue('checkedVariantIds', variantIds)}
        />
      </FormInputGroup>
    </FormWrapper>
  );
}
